/**
 * One definition per figure on the statistics page, in one place.
 *
 * Written to answer "what is this telling me" rather than to define the term, and
 * kept out of the components so the same figure cannot be explained two different
 * ways in two tables. Where a number is easy to misread, the explanation says so
 * explicitly: several of these correct a misreading that came up while building
 * this page.
 */
export type MetricKey =
  | 'sessions'
  | 'conversions'
  | 'revenue'
  | 'average'
  | 'averageRevenue'
  | 'difference'
  | 'weight'
  | 'wilsonCi'
  | 'liftCi'
  | 'zScore'
  | 'pValue'
  | 'sprt'
  | 'significant'
  | 'probabilityBest'
  | 'expectedLoss'
  | 'credibleInterval'
  | 'statisticalPower'
  | 'resolvableEffect'
  | 'timeRemaining'
  | 'requiredSampleSize'
  | 'objective'
  | 'currency'
  | 'evaluationMethod'
  | 'correctionMethod';

export const METRIC_HELP: Record<MetricKey, string> = {
  sessions:
    'Visitors registered for this variant. Counted once per visitor per experiment, not once per visit, so a returning buyer can produce more conversions than sessions.',
  conversions:
    'Completed conversion events for this variant, counted every time they happen. Not restricted by the selected currency, because a conversion is a conversion whatever it was paid in.',
  revenue:
    'Total revenue attributed to this variant in the selected currency. Currencies cannot be added together, so this needs one currency chosen rather than "All".',
  average:
    'Conversions divided by sessions, as a percentage. This is the number the significance test compares between variants.',
  averageRevenue: 'Average revenue per conversion: the order value, not revenue per session.',
  difference:
    'This variant minus the control, in absolute terms. It says nothing on its own about whether the gap is real; that is what the confidence interval and p-value are for.',
  weight:
    'Share of traffic this variant actually received, measured from sessions rather than from the configured split: the two drift apart when a variant is added or paused mid-run.',
  wilsonCi:
    "The range the variant's own true conversion rate most likely falls in, at 95% confidence. A Wilson interval, so it stays inside 0-100% even at small samples where a simple interval would not.",
  liftCi:
    'The range for the difference against the control, at 95% confidence. If the whole range is above zero the variant is genuinely ahead; if it straddles zero, "no difference" is still consistent with the data.',
  zScore:
    'How many standard errors separate this variant from the control. Its sign gives the direction, positive means ahead, and its size is what the p-value is computed from.',
  pValue:
    'How often a difference at least this large would appear by chance if the variant changed nothing. Lower is harder to explain away; below 0.05 is the usual bar.',
  sprt: 'A sequential test that can call a result before a fixed sample size is reached, so a clear winner or a clear non-effect can be acted on early. It answers a different question from the p-value and the two can legitimately disagree.',
  significant:
    'Whether the p-value clears the threshold after the multiple-comparison correction. With a single variant the correction has no effect and the threshold is simply 0.05.',
  probabilityBest:
    'The chance this variant is the best of those tested. Read it directly as a probability: unlike a p-value, it is the answer to the question you actually asked.',
  expectedLoss:
    'What you would expect to give up by shipping this variant if it turns out not to be the best. A small number means the choice is cheap even if it is wrong, which is often what settles a decision.',
  credibleInterval:
    "The range the variant's true conversion rate most likely falls in, from the Bayesian posterior rather than from a sampling argument.",
  statisticalPower:
    'The chance this experiment would detect the difference it is currently measuring, if that difference is real. Below about 80% a null result means "not enough data yet" rather than "no effect".',
  resolvableEffect:
    'The smallest change in conversion rate this much traffic can reliably detect, in percentage points. A difference below this floor will not become significant by waiting at the current traffic level, however long the experiment runs.',
  timeRemaining:
    'How long until enough traffic has accumulated to settle the difference currently being measured, projected from the rate so far. It moves as the measured difference moves, because a smaller difference needs more traffic.',
  requiredSampleSize:
    'Sessions per variant needed to confirm the difference currently being measured, at 95% confidence and 80% power. Sized against the measured difference rather than a fixed target, so it answers "when will this be conclusive".',
  objective: 'What is being measured. Every table and chart below reflects this choice.',
  currency:
    'Scopes every figure to one currency. Conversions are counted across all currencies, but revenue cannot be, adding SEK to EUR gives a meaningless total, so revenue needs a specific currency.',
  evaluationMethod:
    'Frequentist reports how unlikely the result would be if the variant changed nothing. Bayesian reports the probability each variant is best, and what choosing wrongly would cost.',
  correctionMethod:
    'Testing several variants at once raises the chance one looks like a winner by luck. A correction tightens the bar to compensate. It only changes the significant/not-significant verdict: never the p-value, interval or z-score.',
};

/**
 * The significance threshold a correction actually applies, so the control can state
 * its effect instead of only its name.
 *
 * With one comparison all four methods reduce to alpha, which is why switching them
 * appears to do nothing on most experiments: 275 of 306 on the reference data have a
 * single variant. Saying so is more useful than leaving the user to test each option.
 */
export const correctionEffect = (
  method: string,
  comparisons: number,
  alpha = 0.05,
): { threshold: number | null; inert: boolean; note: string } => {
  if (comparisons <= 0) return { threshold: null, inert: true, note: 'No variants to compare yet.' };

  if (comparisons === 1) {
    return {
      threshold: alpha,
      inert: true,
      note: `Only one variant is being compared, so every correction gives the same threshold of ${alpha}. Switching between them cannot change the result.`,
    };
  }

  switch (method) {
    case 'holm-bonferroni':
      return {
        threshold: alpha / comparisons,
        inert: false,
        note: `Across ${comparisons} comparisons the strictest step is ${(alpha / comparisons).toFixed(4)}, relaxing towards ${alpha} for the weaker results. Stops at the first comparison that fails.`,
      };
    case 'benjamini-hochberg':
      return {
        threshold: alpha,
        inert: false,
        note: `Across ${comparisons} comparisons the steps run from ${(alpha / comparisons).toFixed(4)} up to ${alpha}. Controls the share of false positives among the winners rather than the chance of any, so it is less strict than Holm.`,
      };
    case 'sidak': {
      const threshold = 1 - Math.pow(1 - alpha, 1 / comparisons);
      return {
        threshold,
        inert: false,
        note: `Across ${comparisons} comparisons the threshold is ${threshold.toFixed(4)} for every one. Slightly less strict than Bonferroni, and assumes the comparisons are independent.`,
      };
    }
    default:
      return {
        threshold: alpha,
        inert: false,
        note: `Every comparison is judged at ${alpha} with no adjustment. With ${comparisons} comparisons the chance that at least one looks like a winner by luck is about ${Math.round((1 - Math.pow(1 - alpha, comparisons)) * 100)}%.`,
      };
  }
};
